import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { message } from 'antd';
import KindSearchSelect from './KindSearchSelect.jsx';
import {
  ruleLabel,
  confirmMapping,
  reclassifyComponent,
  fetchPageEntities,
} from '../../services/drawingGeometryApi.js';
import { fetchProjectComplianceIssues } from '../../services/complianceIssuesApi.js';
import { getIssueDrawingReviewTarget } from '../compliance-ui/complianceIssuePresenters.js';
import { TAXONOMY_AXIS_OPTIONS } from '../../constants/pilotDrawingKinds';
import { DIMENSIONAL_OUTPUT_KINDS_LC } from '../../constants/drawingTaxonomy';
import { hatLabel } from '../../constants/disciplineHats.js';
import {
  computeComponentMeasurements,
  deriveSheetScale,
} from '../../utils/drawingMeasurements.js';
import { resolveEffectiveSheetScale } from '../../utils/sheetScaleStore.js';
import { componentHatId } from '../../utils/hatVisibility.js';
import {
  buildReviewObject,
  crossRefsForComponent,
} from '../../utils/reviewObject.js';
import { isUserDrawnComponent } from '../../utils/userMarkup.js';
import { suggestKindsForUserMarkup } from '../../utils/drawIntentSuggest.js';

const Wrap = styled.div`
  padding: 16px 18px 24px;
  overflow-y: auto;
  flex: 1;
`;

const Title = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #888;
  letter-spacing: 0.04em;
`;

const Value = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #1f2937;
  margin-top: 6px;
  line-height: 1.35;
  word-break: break-word;
`;

const Section = styled.div`
  margin-top: 16px;
`;

const Label = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #9ca3af;
  margin-bottom: 6px;
`;

const Row = styled.div`
  font-size: 13px;
  color: #4b5563;
  line-height: 1.45;
  margin-top: 10px;
`;

const MeasureRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  font-size: 12px;
  margin-top: 6px;
  color: #4b5563;
`;

const MeasureVal = styled.span`
  font-weight: 600;
  color: #1f2937;
  text-align: right;
`;

const StatusPill = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: ${(p) => p.$bg || '#f3f4f6'};
  color: ${(p) => p.$color || '#374151'};
`;

const ScopeRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
  font-size: 12px;
  color: #4b5563;
`;

const SaveBtn = styled.button`
  margin-top: 16px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  border-radius: 10px;
  background: #e97024;
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TextInput = styled.input`
  width: 100%;
  height: 34px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 13px;
`;

const LinkBtn = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  margin-top: 8px;
  padding: 8px 10px;
  border: 1px solid rgba(233, 112, 36, 0.25);
  border-radius: 8px;
  background: rgba(233, 112, 36, 0.06);
  color: #c45f12;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: rgba(233, 112, 36, 0.12);
  }
`;

const IssueBtn = styled(LinkBtn)`
  border-color: rgba(124, 58, 237, 0.25);
  background: rgba(124, 58, 237, 0.06);
  color: #6d28d9;

  &:hover {
    background: rgba(124, 58, 237, 0.12);
  }
`;

const SuggestRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

const SuggestChip = styled.button`
  border: 1px solid rgba(233, 112, 36, 0.35);
  background: rgba(233, 112, 36, 0.08);
  color: #c45f12;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: rgba(233, 112, 36, 0.16);
  }
`;

function defaultAxisForKind(kind) {
  if (!kind) return 'reference_annotation';
  if (DIMENSIONAL_OUTPUT_KINDS_LC.has(kind.toLowerCase())) return 'dimensional_output';
  return 'reference_annotation';
}

function statusStyle(status) {
  switch (status) {
    case 'auto_validated': return { bg: 'rgba(16,185,129,0.15)', color: '#047857' };
    case 'user_confirmed': return { bg: 'rgba(59,130,246,0.15)', color: '#1d4ed8' };
    case 'pending_user_confirm': return { bg: 'rgba(239,68,68,0.12)', color: '#b91c1c' };
    default: return { bg: 'rgba(245,158,11,0.15)', color: '#b45309' };
  }
}

export default function ObjectInspectorPanel({
  component,
  sheetId,
  sheetMeta,
  sheetIr,
  entityPages,
  projectId,
  requirementId,
  firmId,
  hatId,
  onSaved,
  onLocalOverride,
  onCrossRefNavigate,
  onSelectIssue,
  scaleOverride = null,
}) {
  const [kind, setKind] = useState('');
  const [labelText, setLabelText] = useState('');
  const [scope, setScope] = useState('rule');
  const [saving, setSaving] = useState(false);
  const [pageEntities, setPageEntities] = useState([]);
  const [linkedIssues, setLinkedIssues] = useState([]);

  useEffect(() => {
    if (!component) return;
    setKind(component.kind || '');
    setLabelText(component.label || component.value || '');
    setScope(component.matchedRule ? 'rule' : 'instance');
  }, [component?.id, component?.kind, component?.label, component?.value, component?.matchedRule]);

  useEffect(() => {
    if (!component || !entityPages?.length) {
      setPageEntities([]);
      return undefined;
    }
    const pageIndex = component.pageIndex ?? 0;
    const shard = entityPages.find((p) => p.pageIndex === pageIndex);
    if (!shard?.url) {
      setPageEntities([]);
      return undefined;
    }
    let cancelled = false;
    fetchPageEntities(shard)
      .then((ents) => { if (!cancelled) setPageEntities(ents); })
      .catch(() => { if (!cancelled) setPageEntities([]); });
    return () => { cancelled = true; };
  }, [component?.id, component?.pageIndex, entityPages]);

  useEffect(() => {
    if (!projectId || !component?.id) {
      setLinkedIssues([]);
      return undefined;
    }
    let cancelled = false;
    fetchProjectComplianceIssues(projectId, { limit: 100 })
      .then(({ items }) => {
        if (cancelled) return;
        const matches = (items || []).filter((issue) => {
          const target = getIssueDrawingReviewTarget(issue);
          return target?.componentId === component.id;
        });
        setLinkedIssues(matches);
      })
      .catch(() => {
        if (!cancelled) setLinkedIssues([]);
      });
    return () => { cancelled = true; };
  }, [projectId, component?.id]);

  const crossRefs = useMemo(
    () => crossRefsForComponent(sheetIr, component?.id),
    [sheetIr, component?.id],
  );

  const reviewObject = useMemo(() => {
    if (!component) return null;
    return buildReviewObject({
      component,
      sheetId,
      sheetIrKey: sheetMeta?.irKey,
      linkedIssueIds: linkedIssues.map((i) => i.id),
    });
  }, [component, sheetId, sheetMeta?.irKey, linkedIssues]);

  const assignedHat = useMemo(
    () => componentHatId(component, sheetIr),
    [component, sheetIr],
  );

  const detectedSheetScale = useMemo(() => {
    const comps = sheetIr?.components || [];
    if (!comps.length) return null;
    return deriveSheetScale(comps, pageEntities, component?.pageIndex ?? null);
  }, [sheetIr?.components, pageEntities, component?.pageIndex]);

  const sheetScale = useMemo(
    () => resolveEffectiveSheetScale(detectedSheetScale, scaleOverride),
    [detectedSheetScale, scaleOverride],
  );

  const measurementRows = useMemo(() => (
    computeComponentMeasurements(component, {
      ir: sheetIr,
      entities: pageEntities,
      sheetScale,
    })
  ), [component, sheetIr, pageEntities, sheetScale]);

  const kindSuggestions = useMemo(() => {
    if (!component || !isUserDrawnComponent(component)) return [];
    return suggestKindsForUserMarkup(component);
  }, [component]);

  if (!component) {
    return (
      <Wrap>
        <div style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.55 }}>
          Select an object from the sheet list, enable the Labels lens, or click a label on the drawing.
        </div>
      </Wrap>
    );
  }

  const axis = TAXONOMY_AXIS_OPTIONS.find((o) => o.value === component.taxonomyAxis);
  const st = statusStyle(component.mappingStatus);
  const lifecycleLabel = reviewObject?.lifecycleState?.replace(/_/g, ' ') || 'draft';
  const displayValue = labelText || component.label || component.value || component.id;
  const kindDirty = kind && kind !== component.kind;
  const labelDirty = labelText !== (component.label || component.value || '');
  const canSave = kindDirty || labelDirty;

  const handleSave = async () => {
    if (!canSave || !kind) {
      message.warning('Pick a label type before saving.');
      return;
    }

    const taxonomyAxis = defaultAxisForKind(kind);
    const patches = {
      kind,
      taxonomyAxis,
      ...(labelDirty ? { label: labelText, value: labelText } : {}),
      mappingStatus: 'user_confirmed',
    };

    if (isUserDrawnComponent(component)) {
      onLocalOverride?.(component.id, patches);
      message.success('Intent saved on this markup.');
      return;
    }

    const useRuleConfirm = scope === 'rule' && component.matchedRule;

    if (!useRuleConfirm) {
      if (!sheetMeta?.irKey) {
        onLocalOverride?.(component.id, patches);
        message.warning('Geometry API unavailable — saved for this session only.');
        return;
      }

      setSaving(true);
      try {
        const result = await reclassifyComponent({
          irKey: sheetMeta.irKey,
          componentId: component.id,
          projectId,
          requirementId,
          ...patches,
        });
        message.success('Saved this object to the drawing record.');
        onSaved?.({
          componentId: component.id,
          component: result.component,
          ...patches,
        });
      } catch (err) {
        console.error('[ObjectInspector] reclassify error:', err);
        message.error(err.message || 'Failed to save label type');
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!sheetMeta?.irKey || !projectId || !requirementId) {
      onLocalOverride?.(component.id, patches);
      message.warning('Geometry API unavailable — saved for this session only.');
      return;
    }

    const isCorrection = component.mappingStatus !== 'pending_user_confirm';

    setSaving(true);
    try {
      const result = await confirmMapping({
        irKey: sheetMeta.irKey,
        matchedRule: component.matchedRule,
        kind,
        taxonomyAxis,
        componentId: component.id,
        projectId,
        requirementId,
        firmId,
        hatId,
        applyAcrossRequirement: true,
        relabelAll: isCorrection,
      });
      message.success(
        `Saved "${kind}" for ${result.instancesConfirmed || 1} instance(s).`
      );
      onSaved?.({ componentId: component.id, ...patches, instancesConfirmed: result.instancesConfirmed });
    } catch (err) {
      console.error('[ObjectInspector] save error:', err);
      message.error(err.message || 'Failed to save label type');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Wrap>
      <Title>Object inspector</Title>
      <Value>{displayValue}</Value>

      <Section>
        <Label>Label type (kind)</Label>
        <KindSearchSelect value={kind} onChange={setKind} />
        {isUserDrawnComponent(component) && kindSuggestions.length > 0 && (
          <SuggestRow>
            {kindSuggestions.map((suggestion) => (
              <SuggestChip
                key={suggestion}
                type="button"
                onClick={() => setKind(suggestion)}
              >
                {suggestion}
              </SuggestChip>
            ))}
          </SuggestRow>
        )}
      </Section>

      {isUserDrawnComponent(component) && component.mappingStatus === 'pending_user_confirm' && (
        <Row style={{ color: '#b45309', fontSize: 12 }}>
          Assign intent below, then save. This object is stored locally until markup API is wired.
        </Row>
      )}

      <Section>
        <Label>Display text</Label>
        <TextInput
          value={labelText}
          onChange={(e) => setLabelText(e.target.value)}
          placeholder="Label or dimension value"
        />
      </Section>

      {component.matchedRule && (
        <Section>
          <Label>Apply to</Label>
          <ScopeRow>
            <label>
              <input
                type="radio"
                name="scope"
                checked={scope === 'rule'}
                onChange={() => setScope('rule')}
              />
              {' '}All similar ({ruleLabel(component.matchedRule)})
            </label>
            <label>
              <input
                type="radio"
                name="scope"
                checked={scope === 'instance'}
                onChange={() => setScope('instance')}
              />
              {' '}This object only
            </label>
          </ScopeRow>
        </Section>
      )}

      {measurementRows?.length > 0 && (
        <Section>
          <Label>Measurements</Label>
          {measurementRows.map((row) => (
            <MeasureRow key={row.label}>
              <span>{row.label}</span>
              <MeasureVal>{row.value}</MeasureVal>
            </MeasureRow>
          ))}
          {sheetScale?.ratio && (
            <MeasureRow>
              <span>Sheet scale</span>
              <MeasureVal>{sheetScale.ratio.label}</MeasureVal>
            </MeasureRow>
          )}
        </Section>
      )}

      <Row>
        <Label>Taxonomy</Label>
        {axis?.label || component.taxonomyAxis || defaultAxisForKind(kind)}
      </Row>
      <Row>
        <Label>Status</Label>
        <StatusPill $bg={st.bg} $color={st.color}>
          {component.mappingStatus?.replace(/_/g, ' ') || 'unknown'}
        </StatusPill>
      </Row>
      <Row>
        <Label>Lifecycle</Label>
        {lifecycleLabel}
      </Row>
      {(assignedHat || hatId) && (
        <Row>
          <Label>Discipline hat</Label>
          {hatLabel(assignedHat || hatId)}
        </Row>
      )}
      {sheetId && (
        <Row>
          <Label>Location</Label>
          {sheetId}
          {component.pageIndex != null && ` · page ${component.pageIndex + 1}`}
        </Row>
      )}
      {component.unknownReason && (
        <Row>
          <Label>Note</Label>
          {component.unknownReason}
        </Row>
      )}

      {crossRefs.length > 0 && (
        <Section>
          <Label>Cross-references</Label>
          {crossRefs.map((ref) => (
            <LinkBtn
              key={`${ref.componentId}-${ref.targetSheet}-${ref.targetDetail || ''}`}
              type="button"
              onClick={() => onCrossRefNavigate?.({
                targetSheet: ref.targetSheet,
                targetDetail: ref.targetDetail,
                sourceComponentId: component.id,
              })}
            >
              Go to {ref.targetDetail ? `${ref.targetDetail} / ` : ''}{ref.targetSheet || 'detail'}
            </LinkBtn>
          ))}
        </Section>
      )}

      {linkedIssues.length > 0 && (
        <Section>
          <Label>Linked compliance issues</Label>
          {linkedIssues.map((issue) => (
            <IssueBtn
              key={issue.id}
              type="button"
              onClick={() => onSelectIssue?.(issue)}
            >
              {issue.identifiedIssue || issue.topic || issue.id}
            </IssueBtn>
          ))}
        </Section>
      )}

      <SaveBtn type="button" disabled={!canSave || saving} onClick={handleSave}>
        {saving ? 'Saving…' : 'Save label type'}
      </SaveBtn>
    </Wrap>
  );
}

